import React, { useState, useEffect, useRef, useCallback } from 'react';
import eventBus from '../core/EventBus';

function CollageImageUploader({ canvasManager, collageId, placeholderCount, onClose, onDiscard }) {
  const [slots, setSlots] = useState(() =>
    Array.from({ length: placeholderCount }, () => ({ file: null, url: null }))
  );
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showUploadDisclaimer, setShowUploadDisclaimer] = useState(false);
  const [disclaimerAction, setDisclaimerAction] = useState(null);
  const fileInputRefs = useRef([]);
  const bulkInputRef = useRef(null);

  // Helper to gate upload actions behind the disclaimer
  const requireDisclaimer = useCallback((action) => {
    if (sessionStorage.getItem('selenny-upload-accepted')) {
      action();
    } else {
      setDisclaimerAction(() => action);
      setShowUploadDisclaimer(true);
    }
  }, []);

  // Sync placeholder count changes
  useEffect(() => {
    setSlots(prev => {
      if (prev.length === placeholderCount) return prev;
      const next = Array.from({ length: placeholderCount }, (_, i) =>
        prev[i] || { file: null, url: null }
      );
      return next;
    });
  }, [placeholderCount]);

  // Listen for external collage:image-set events
  useEffect(() => {
    const handler = (data) => {
      if (data.collageId !== collageId) return;
      setSlots(prev => {
        const next = [...prev];
        if (data.index >= 0 && data.index < next.length) {
          next[data.index] = {
            file: data.file || null,
            url: data.url || null,
          };
        }
        return next;
      });
    };

    const unsub = eventBus.on('collage:image-set', handler);
    return () => unsub();
  }, [collageId]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      slots.forEach(slot => {
        if (slot.url) {
          URL.revokeObjectURL(slot.url);
        }
      });
    };
  }, []); // eslint-disable-line

  const assignImageToSlot = useCallback((index, file) => {
    const objectUrl = URL.createObjectURL(file);

    setSlots(prev => {
      const next = [...prev];
      // Revoke old URL if present
      if (next[index]?.url) {
        URL.revokeObjectURL(next[index].url);
      }
      next[index] = { file, url: objectUrl };
      return next;
    });

    if (canvasManager?.setCollageImage) {
      canvasManager.setCollageImage(collageId, index, objectUrl);
    }
  }, [canvasManager, collageId]);

  const removeImage = useCallback((index) => {
    setSlots(prev => {
      const next = [...prev];
      if (next[index]?.url) {
        URL.revokeObjectURL(next[index].url);
      }
      next[index] = { file: null, url: null };
      return next;
    });

    if (canvasManager?.setCollageImage) {
      canvasManager.setCollageImage(collageId, index, null);
    }
  }, [canvasManager, collageId]);

  const handleFileChange = useCallback((index, e) => {
    const file = e.target.files?.[0];
    if (file) {
      assignImageToSlot(index, file);
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  }, [assignImageToSlot]);

  const handleBulkUpload = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Find empty slot indices
    const emptyIndices = [];
    slots.forEach((slot, i) => {
      if (!slot.url) emptyIndices.push(i);
    });

    // Assign files to empty slots in order
    files.forEach((file, i) => {
      if (i < emptyIndices.length) {
        assignImageToSlot(emptyIndices[i], file);
      }
    });

    e.target.value = '';
  }, [slots, assignImageToSlot]);

  const handleAdjustImage = useCallback((index) => {
    if (!canvasManager?.canvas) return;

    const objects = canvasManager.canvas.getObjects();
    const img = objects.find(
      o => o.isCollageImage && o.collageId === collageId && o.collageIndex === index
    );
    if (img) {
      canvasManager.canvas.setActiveObject(img);
      canvasManager.canvas.renderAll();
      eventBus.emit('collage:image-selected', { collageId, index });
    }
  }, [canvasManager, collageId]);

  // Drag & drop handlers
  const handleDragOver = useCallback((index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      if (sessionStorage.getItem('selenny-upload-accepted')) {
        assignImageToSlot(index, file);
      } else {
        setDisclaimerAction(() => () => assignImageToSlot(index, file));
        setShowUploadDisclaimer(true);
      }
    }
  }, [assignImageToSlot]);

  const filledCount = slots.filter(s => s.url).length;

  return (
    <div
      className="fixed z-50 flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200"
      style={{ right: 300, top: 80, width: 280, maxHeight: 'calc(100vh - 120px)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-800">Fotos zuordnen</span>
          <span className="text-xs text-gray-400 mt-0.5">
            {filledCount} von {placeholderCount} Plätzen belegt
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onClose}
            className="flex items-center justify-center px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors text-[11px] text-gray-400 hover:text-gray-600"
            title="Minimieren — Klick auf ein Collage-Feld öffnet das Menü erneut"
          >
            Minimieren
          </button>
        </div>
      </div>

      {/* Progress */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {filledCount} von {placeholderCount} Fotos
          </span>
          <div className="flex-1 ml-3 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${placeholderCount > 0 ? (filledCount / placeholderCount) * 100 : 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Slots Grid */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="grid grid-cols-2 gap-2.5">
          {slots.map((slot, index) => (
            <div key={index} className="relative">
              {/* Hidden file input */}
              <input
                ref={el => (fileInputRefs.current[index] = el)}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(index, e)}
              />

              {slot.url ? (
                /* Filled slot */
                <div className="relative group rounded-lg overflow-hidden" style={{ width: '100%', paddingBottom: '100%' }}>
                  <img
                    src={slot.url}
                    alt={`Foto ${index + 1}`}
                    className="absolute inset-0 w-full h-full object-cover rounded-lg"
                  />

                  {/* Slot number badge */}
                  <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                    {index + 1}
                  </div>

                  {/* Remove button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                    className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600"
                    title="Foto entfernen"
                  >
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M1 1l6 6M7 1L1 7" />
                    </svg>
                  </button>

                  {/* Adjust image overlay */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAdjustImage(index); }}
                    className="absolute inset-x-0 bottom-0 py-1.5 bg-black/60 text-white text-[10px] font-medium text-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm"
                    title="Bild auf der Leinwand auswählen und anpassen"
                  >
                    Bild anpassen
                  </button>
                </div>
              ) : (
                /* Empty slot */
                <div
                  onClick={() => requireDisclaimer(() => fileInputRefs.current[index]?.click())}
                  onDragOver={(e) => handleDragOver(index, e)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(index, e)}
                  className={`
                    relative cursor-pointer rounded-lg border-2 border-dashed transition-all duration-200
                    flex flex-col items-center justify-center
                    ${dragOverIndex === index
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-300 bg-[#f8fafc] hover:border-blue-400 hover:bg-blue-50/50'
                    }
                  `}
                  style={{ width: '100%', paddingBottom: '100%' }}
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {/* Slot number */}
                    <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 text-xs font-bold flex items-center justify-center mb-1.5">
                      {index + 1}
                    </div>

                    {/* Upload icon */}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-gray-400 mb-1">
                      <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" />
                      <circle cx="8.5" cy="8.5" r="2" stroke="currentColor" strokeWidth="1.5" />
                      <path d="M3 16l4.5-4.5a1 1 0 011.4 0L12 14.5l2.1-2.1a1 1 0 011.4 0L21 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>

                    <span className="text-[10px] text-gray-400 font-medium">Foto</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer - Bulk upload + discard */}
      <div className="px-3 py-3 border-t border-gray-100 flex flex-col gap-2">
        <input
          ref={bulkInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleBulkUpload}
        />
        <button
          onClick={() => requireDisclaimer(() => bulkInputRef.current?.click())}
          disabled={filledCount === placeholderCount}
          className={`
            w-full py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200
            flex items-center justify-center gap-2
            ${filledCount === placeholderCount
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700 shadow-sm'
            }
          `}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <path d="M8 3v10M3 8h10" />
          </svg>
          Mehrere Fotos auswählen
        </button>
        <button
          onClick={() => {
            if (canvasManager?.removeCollage) {
              canvasManager.removeCollage(collageId);
            }
            if (onDiscard) onDiscard();
            else onClose();
          }}
          className="w-full py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 active:bg-red-100"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/>
          </svg>
          Collage verwerfen
        </button>
      </div>

      {/* Upload Disclaimer Modal */}
      {showUploadDisclaimer && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          onClick={() => { setShowUploadDisclaimer(false); setDisclaimerAction(null); }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Card */}
          <div
            className="relative bg-white dark:bg-[#1c1c1e] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 mx-4"
            style={{ maxWidth: 480, width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <svg className="w-7 h-7 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-3">
              Hinweis zum Bild-Upload
            </h3>

            {/* Body */}
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              Mit dem Hochladen eines Bildes best&auml;tigen Sie:
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 mb-4">
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 flex-shrink-0">&bull;</span>
                <span>Sie besitzen die vollst&auml;ndigen Bildrechte oder haben die ausdr&uuml;ckliche Genehmigung des Rechteinhabers.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 flex-shrink-0">&bull;</span>
                <span>Das Bild enth&auml;lt keine anst&ouml;&szlig;igen, diskriminierenden, gewaltverherrlichenden oder menschenverachtenden Inhalte.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 flex-shrink-0">&bull;</span>
                <span>Sie tragen die alleinige Verantwortung f&uuml;r den hochgeladenen Inhalt — sowohl in rechtlicher als auch in qualitativer Hinsicht.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 flex-shrink-0">&bull;</span>
                <span>Selenny&reg; &uuml;bernimmt keine Haftung f&uuml;r Inhalte, die von Nutzern hochgeladen werden.</span>
              </li>
            </ul>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-5">
              Bei Verst&ouml;&szlig;en gegen diese Richtlinien behalten wir uns vor, Auftr&auml;ge abzulehnen.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => { setShowUploadDisclaimer(false); setDisclaimerAction(null); }}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  sessionStorage.setItem('selenny-upload-accepted', '1');
                  setShowUploadDisclaimer(false);
                  const action = disclaimerAction;
                  setDisclaimerAction(null);
                  if (action) setTimeout(() => action(), 100);
                }}
                className="flex-1 py-2.5 px-4 rounded-xl text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700 transition-colors shadow-sm"
              >
                Verstanden & Akzeptieren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CollageImageUploader;
